import * as React from 'react';

import {
  InventoryItemsModalMode,
  InventoryTagData,
} from './_InventoryItemsModal';
import { InventoryItemsModalGroupView } from './InventoryItemsModalGroupView';
import { InventoryItemsModalItemView } from './InventoryItemsModalItemView';
import { InventoryItemsModalRootView } from './InventoryItemsModalRootView';
import { InventoryTagOption } from './InventoryItemsModalTagsGrid';

import { InventoryItemRecord } from '~/modules/trade/types/load';

export type InventoryItemsModalContentProps = {
  mode: InventoryItemsModalMode;
  effectiveColorScheme: 'light' | 'dark';
  searchTerm: string;
  noResultsFound: boolean;
  selectedMaterialGroupId: string | null;
  selectedItemId: string | null;
  selectedGroupName: string;

  // Items mode data
  inventoryItems: InventoryItemRecord[];
  materialSearchResults: InventoryItemRecord[];
  groupSearchResults: InventoryItemRecord[];

  // Tags mode data
  tagSearchResults: InventoryTagOption[];
  tagGroupSearchResults: InventoryItemRecord[];
  tagsForSelectedGroup: InventoryTagOption[];
  tagsForSelectedItem: InventoryTagOption[];

  // All groups
  itemGroups: InventoryItemRecord[];

  // Tags-by-item map
  inventoryTagOptionsByItemId: Map<string, unknown[]>;

  // Selection state
  selectedId?: string | null;

  // Highlighted SO materials
  highlightedMaterialIds?: string[];
  highlightedSectionTitle?: string;

  // Callbacks
  onSelectGroup: (groupId: string) => void;
  onSelectMaterial: (materialId: string) => void;
  onSelectItem: (itemId: string) => void;
  onSelectTag: (tagId: string, tagData: InventoryTagData) => void;
};

//
// ⚛️ ⚛️ ⚛️ INVENTORY ITEMS MODAL CONTENT ⚛️ ⚛️ ⚛️
//
export const InventoryItemsModalContent = (
  props: InventoryItemsModalContentProps,
) => {
  const { inventoryItems, selectedMaterialGroupId, selectedItemId, mode } =
    props;

  // Memo
  const itemsForSelectedGroup = React.useMemo(() => {
    if (!selectedMaterialGroupId) return [];
    return inventoryItems.filter(
      (item) => item.itemGroupId === selectedMaterialGroupId,
    );
  }, [inventoryItems, selectedMaterialGroupId]);

  const selectedItem = React.useMemo(() => {
    if (!selectedItemId) return null;
    return inventoryItems.find((item) => item.id === selectedItemId) ?? null;
  }, [inventoryItems, selectedItemId]);

  const isAtItemLevel =
    mode === 'tags' && selectedMaterialGroupId && selectedItemId;

  if (isAtItemLevel && selectedItem) {
    return (
      <div className="@InventoryItemsModalContent flex flex-col gap-6">
        <InventoryItemsModalItemView
          selectedItem={selectedItem}
          tagsForItem={props.tagsForSelectedItem}
          selectedId={props.selectedId}
          onSelectTag={props.onSelectTag}
        />
      </div>
    );
  }

  if (selectedMaterialGroupId) {
    return (
      <div className="@InventoryItemsModalContent flex flex-col gap-6">
        <InventoryItemsModalGroupView
          mode={mode}
          selectedGroupId={selectedMaterialGroupId}
          selectedGroupName={props.selectedGroupName}
          itemsForGroup={itemsForSelectedGroup}
          tagsForGroup={props.tagsForSelectedGroup}
          inventoryTagOptionsByItemId={props.inventoryTagOptionsByItemId}
          selectedId={props.selectedId}
          highlightedMaterialIds={props.highlightedMaterialIds}
          highlightedSectionTitle={props.highlightedSectionTitle}
          onSelectMaterial={props.onSelectMaterial}
          onSelectItem={props.onSelectItem}
          onSelectTag={props.onSelectTag}
        />
      </div>
    );
  }

  return (
    <div className="@InventoryItemsModalContent flex flex-col gap-6">
      <InventoryItemsModalRootView
        mode={mode}
        effectiveColorScheme={props.effectiveColorScheme}
        searchTerm={props.searchTerm}
        noResultsFound={props.noResultsFound}
        materialSearchResults={props.materialSearchResults}
        groupSearchResults={props.groupSearchResults}
        tagSearchResults={props.tagSearchResults}
        tagGroupSearchResults={props.tagGroupSearchResults}
        itemGroups={props.itemGroups}
        selectedMaterialGroupId={selectedMaterialGroupId}
        selectedId={props.selectedId}
        onSelectGroup={props.onSelectGroup}
        onSelectMaterial={props.onSelectMaterial}
        onSelectTag={props.onSelectTag}
      />
    </div>
  );
};
